// CameraPlayer.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Dialog,
  IconButton,
} from "@mui/material";
import { Loader2, WifiOff, RefreshCw, Play, Image as ImageIcon, X } from "lucide-react";

function CameraPlayer({
  device,
  camera,
  cameraType,
  sessionId,
  playbackUrl,

  // ✅ NEW
  latestSnapshotId,          // string (CameraMedia _id)
  latestSnapshotCreatedAt,   // date string
  backendBaseUrl,            // optional override
}) {
  const [connectionState, setConnectionState] = useState("connecting");
  const [retryCount, setRetryCount] = useState(0);
  const [openSnap, setOpenSnap] = useState(false);
  const videoRef = useRef(null);

  const cameraTypeLabels = {
    front: "Front Camera",
    rear: "Rear Camera",
    cabin: "Interior Camera",
    side_left: "Left Side",
    side_right: "Right Side",
    trailer: "Trailer",
    other: "Other",
  };

  const srcUrl = useMemo(() => playbackUrl || "", [playbackUrl, retryCount]);

  const base =
    backendBaseUrl ||
    process.env.REACT_APP_BACKEND_URL ||
    "http://localhost:7600/api";

  const snapshotUrl = useMemo(() => {
    if (!latestSnapshotId) return "";
    return `${base}/jt808/media/${latestSnapshotId}/download`;
  }, [base, latestSnapshotId]);

  useEffect(() => {
    if (!device) {
      setConnectionState("no-device");
      return;
    }
    if (!sessionId) {
      setConnectionState("no-stream");
      return;
    }
    if (!srcUrl) {
      setConnectionState("connecting");
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    setConnectionState("connecting");

    let hls;
    const cleanup = () => {
      try {
        if (hls) hls.destroy();
      } catch {}
      hls = null;
    };

    // Native HLS (Safari)
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = srcUrl;

      const onLoaded = () => setConnectionState("connected");
      const onError = () => setConnectionState("failed");

      video.addEventListener("loadedmetadata", onLoaded);
      video.addEventListener("error", onError);

      video.play().catch(() => {});

      return () => {
        video.removeEventListener("loadedmetadata", onLoaded);
        video.removeEventListener("error", onError);
      };
    }

    // Hls.js
    if (Hls.isSupported()) {
      hls = new Hls({
        lowLatencyMode: true,
        backBufferLength: 30,
      });

      hls.loadSource(srcUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
        setConnectionState("connected");
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) {
          cleanup();
          setConnectionState("failed");
        }
      });

      return () => cleanup();
    }

    setConnectionState("failed");
  }, [device, sessionId, srcUrl]);

  const handleRetry = () => {
    setRetryCount((p) => p + 1);
    setConnectionState("connecting");
  };

  const overlayLabel =
    connectionState === "connected"
      ? "LIVE"
      : connectionState === "connecting"
      ? "CONNECTING"
      : "OFFLINE";

  const hasSnapshot = Boolean(latestSnapshotId);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
        bgcolor: "common.black",
      }}
    >
      {/* CONTENT */}
      {connectionState === "connected" ? (
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          <video
            ref={videoRef}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            controls
            muted
            playsInline
            autoPlay
          />
        </Box>
      ) : connectionState === "connecting" ? (
        <Centered>
          <Loader2 size={40} className="spin" />
          <Typography fontWeight={900} sx={{ mt: 1 }}>
            Connecting to stream...
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.75)">
            {camera?.name || cameraTypeLabels[cameraType] || cameraType}
          </Typography>
        </Centered>
      ) : connectionState === "failed" ? (
        <Centered>
          <WifiOff size={40} />
          <Typography fontWeight={900} sx={{ mt: 1 }}>
            Connection Failed
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.75)">
            Unable to connect to camera stream
          </Typography>
          <Button
            onClick={handleRetry}
            variant="contained"
            sx={{ mt: 1.5, borderRadius: 2 }}
            startIcon={<RefreshCw size={16} />}
          >
            Retry
          </Button>
        </Centered>
      ) : connectionState === "no-stream" ? (
        <Centered>
          <Play size={40} />
          <Typography fontWeight={900} sx={{ mt: 1 }}>
            Stream Not Started
          </Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.75)">
            Click Start to begin streaming
          </Typography>
        </Centered>
      ) : (
        <Centered>
          <Typography fontWeight={900}>No Device</Typography>
          <Typography variant="body2" color="rgba(255,255,255,0.75)">
            Assign a device to this slot
          </Typography>
        </Centered>
      )}

      {/* OVERLAY TOP */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          position: "absolute",
          top: 10,
          left: 10,
          right: 10,
          justifyContent: "space-between",
          pointerEvents: "none",
        }}
      >
        <Chip
          size="small"
          label={overlayLabel}
          color={connectionState === "connected" ? "success" : "default"}
          variant={connectionState === "connected" ? "filled" : "outlined"}
          sx={{
            bgcolor:
              connectionState === "connected"
                ? "rgba(46,125,50,0.9)"
                : "rgba(0,0,0,0.25)",
            color: "white",
            borderColor: "rgba(255,255,255,0.25)",
          }}
        />

        <Stack direction="row" spacing={1} sx={{ pointerEvents: "auto" }}>
          {/* ✅ Snapshot chip */}
          <Chip
            size="small"
            icon={<ImageIcon size={14} />}
            label={hasSnapshot ? "SNAPSHOT" : "NO SNAP"}
            onClick={hasSnapshot ? () => setOpenSnap(true) : undefined}
            variant="outlined"
            sx={{
              cursor: hasSnapshot ? "pointer" : "default",
              bgcolor: "rgba(0,0,0,0.25)",
              color: "white",
              borderColor: "rgba(255,255,255,0.25)",
              "& .MuiChip-icon": { color: "white" },
              opacity: hasSnapshot ? 1 : 0.6,
            }}
          />

          <Chip
            size="small"
            label={
              cameraTypeLabels[camera?.cameraType] ||
              cameraTypeLabels[cameraType] ||
              cameraType
            }
            variant="outlined"
            sx={{
              bgcolor: "rgba(0,0,0,0.25)",
              color: "white",
              borderColor: "rgba(255,255,255,0.25)",
            }}
          />
        </Stack>
      </Stack>

      {/* ✅ THUMBNAIL PREVIEW (bottom-right) */}
      {hasSnapshot && snapshotUrl && (
        <Box
          onClick={() => setOpenSnap(true)}
          sx={{
            position: "absolute",
            right: 12,
            bottom: 12,
            width: 168,
            height: 96,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid rgba(255,255,255,0.25)",
            cursor: "pointer",
            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            bgcolor: "rgba(0,0,0,0.15)",
          }}
        >
          <img
            src={snapshotUrl}
            alt="Latest snapshot"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />

          {/* timestamp overlay */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              px: 1,
              py: 0.5,
              fontSize: 11,
              color: "white",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))",
            }}
          >
            {latestSnapshotCreatedAt
              ? new Date(latestSnapshotCreatedAt).toLocaleString()
              : "Latest"}
          </Box>
        </Box>
      )}

      {/* ✅ SNAPSHOT VIEWER */}
      <Dialog
        open={openSnap}
        onClose={() => setOpenSnap(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "min(1100px, 95vw)",
            bgcolor: "black",
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setOpenSnap(false)}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 2,
              color: "white",
              bgcolor: "rgba(0,0,0,0.35)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
            }}
          >
            <X size={18} />
          </IconButton>

          <img
            src={snapshotUrl}
            alt="Snapshot"
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "82vh",
              objectFit: "contain",
              display: "block",
            }}
          />

          <Box sx={{ p: 1.25, color: "white" }}>
            <Typography fontWeight={800}>
              {camera?.name || cameraTypeLabels[cameraType] || "Snapshot"}
            </Typography>
            {latestSnapshotCreatedAt && (
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {new Date(latestSnapshotCreatedAt).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>
      </Dialog>

      <style>{`
        .spin { animation: spin 0.9s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </Box>
  );
}

function Centered({ children }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        p: 2,
        color: "white",
        flexDirection: "column",
      }}
    >
      {children}
    </Box>
  );
}

export default CameraPlayer;
